import clsx from 'clsx'
import isEqual from 'lodash/isEqual'
import React, { useEffect, useRef, useState } from 'react'
import { Control, UseFormGetValues, UseFormSetValue } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'

import s from './styles.module.scss'

import { DatePickerModeEnum } from './date-picker.enum'
import { getDatePickerClassNameByEnum } from './date-picker.utils'
import { DatePickerCalendar } from './DatePickerCalendar'
import { DatePickerInputField } from './DatePickerInputField'
import { DatePickerMobileFooter } from './DatePickerMobileFooter'
import { ModalPicker, ModalPickerRef } from '../modal-picker'
import { ControlledCommons } from '../../atoms/fields/common.interface'
import { useTravelTypeContext } from '../../contexts'
import { IDatesCalendarProps } from '../../organisms/dates-calendar/dates-calendar'
import { HouseContext, TravelTypeEnum } from '../../../lib/graphql'

export interface IControlledDatePickerRangeProps extends ControlledCommons {
  control: Control<any, any>
  rules: any
  name: any
  nameHours?: string
  getAndSetter?: {
    setValue: UseFormSetValue<any>
    getValues: UseFormGetValues<any>
  }
  message?: string
  isCentered?: boolean
  className?: string
  containerClassName?: string
  modalClassName?: string
  house?: Pick<HouseContext, 'arrival' | 'departure'>
  onClose?: () => void
  dateCalendarProps: IDatesCalendarProps
  isMobile?: boolean
  alternativeClassName?: string
  mode?: DatePickerModeEnum
  hideLabels?: boolean
  innerModalClassName?: string
  revampPicker?: boolean
  isFullScreen?: boolean
  isOpen?: boolean
  onValid?: () => void
  shouldDisplayHourPickers?: boolean
}

export interface IDatePickerDates {
  startDate: Date
  endDate: Date
}

export default function DatePicker({
  control,
  rules,
  name,
  nameHours,
  getAndSetter,
  isCentered = true,
  className,
  modalClassName,
  containerClassName,
  house,
  onClose,
  dateCalendarProps,
  isMobile = false,
  alternativeClassName = 'default',
  mode = DatePickerModeEnum.DEFAULT,
  hideLabels = false,
  innerModalClassName,
  revampPicker = false,
  isFullScreen,
  isOpen = false,
  onValid,
  shouldDisplayHourPickers,
}: IControlledDatePickerRangeProps) {
  const ref = useRef<ModalPickerRef>(null)
  const { travelType } = useTravelTypeContext()

  const [currentTravelType, setCurrentTravelType] =
    useState<TravelTypeEnum>(travelType)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isDatesSelected, setIsDatesSelected] = useState(false)

  const onDatesSelected = (value: boolean) => {
    setIsDatesSelected(value)
  }

  useEffect(() => {
    if (currentTravelType !== travelType && getAndSetter) {
      if (!isEqual(getAndSetter.getValues(name), {})) {
        getAndSetter.setValue(name, {})
        onDatesSelected(false)
      }
    }
    setCurrentTravelType(travelType)
  }, [travelType])

  useEffect(() => {
    if (isOpen) {
      ref.current?.setShowModal(true)
    }
  }, [isOpen])

  const handleOpen = () => setIsModalOpen(true)
  const handleClose = () => {
    const singleOrMultiDatesTravelType = [
      TravelTypeEnum.DAILY_TRIP,
      TravelTypeEnum.TEAM_BUILDING,
      TravelTypeEnum.BUSINESS_MEAL,
      TravelTypeEnum.TRANSPORT,
    ].includes(travelType)
    const dates = getAndSetter?.getValues(name)
    // If travelType is daily trip and startDate is set but endDate isn't when modal is closed we set endDate to startDate
    if (singleOrMultiDatesTravelType && dates?.startDate && !dates?.endDate) {
      getAndSetter?.setValue(name, { ...dates, endDate: dates.startDate })
    }
    setIsModalOpen(false)
    onClose?.()
  }

  const onCloseModal = () => {
    handleClose()
    ref.current?.setShowModal(false)
  }

  return (
    <ModalPicker
      revampPicker={revampPicker}
      isFullScreen={isFullScreen}
      dataGtm={
        revampPicker ? 'date_arrivee_depart_new_search' : 'date_arrivee_depart'
      }
      dataMixpanel={{
        name: 'date_arrivee_depart',
        data: { newSearch: revampPicker },
      }}
      control={control}
      rules={rules}
      name={name}
      ref={ref}
      mobileTitle={
        <div>
          <FormattedMessage
            id='ui.DatePicker.selectDates'
            defaultMessage='Sélectionnez vos dates'
          />
        </div>
      }
      mobileFooter={
        revampPicker ? (
          <DatePickerMobileFooter
            onClick={onCloseModal}
            disabled={!isDatesSelected}
          />
        ) : undefined
      }
      isCentered={isCentered}
      containerClassName={clsx(
        containerClassName
          ? containerClassName
          : s[getDatePickerClassNameByEnum(mode) + 'Container'],
        s[`${alternativeClassName}_container`],
      )}
      modalClassName={clsx(
        modalClassName ? modalClassName : s[getDatePickerClassNameByEnum(mode)],
        s[`${alternativeClassName}_modal`],
        '',
      )}
      innerModalClassName={innerModalClassName}
      renderContentModal={(field) => (
        <DatePickerCalendar
          field={field}
          control={control}
          dateCalendarProps={dateCalendarProps}
          closeModal={() =>
            ref && ref.current && ref.current.setShowModal(false)
          }
          onDatesSelected={onDatesSelected}
          house={house}
          isMobile={isMobile}
          nameHours={nameHours}
          revampPicker={revampPicker}
          onValid={onValid}
          mode={mode}
          shouldDisplayHourPickers={shouldDisplayHourPickers}
        />
      )}
      className={clsx(className, s[alternativeClassName])}
      onClose={handleClose}
      onOpen={handleOpen}
      renderInputField={(field) => (
        <DatePickerInputField
          mode={mode}
          isModalOpen={isModalOpen}
          revampPicker={revampPicker}
          isDatesSelected={isDatesSelected}
          isMobile={isMobile}
          hideLabels={hideLabels}
          field={field}
          alternativeClassName={alternativeClassName}
        />
      )}
    />
  )
}
